import { Injectable } from '@angular/core';
import { Field, FileFormat } from '../models';
import { MessageService } from 'primeng/api';
import { FILE_FORMATS } from '@shared-consts/file-formats.const';
// file-validation.service.ts
@Injectable({
  providedIn: 'root',
})
export class FileValidationService {
  constructor(private messageService: MessageService) {}

  getFileFormat(formatType: string): FileFormat | null {
    const format = FILE_FORMATS[formatType as keyof typeof FILE_FORMATS];
    return format ? (format as FileFormat) : null;
  }

  // Métodos privados de validación...
  private validateLine(
    line: string,
    fields: Field[],
    lineType: string,
    expectedLength: number,
  ): string[] {
    const errors: string[] = [];

    // Validar longitud de línea
    if (line.length < expectedLength) {
      errors.push(
        `${lineType}: Longitud incorrecta. Esperado: ${expectedLength}, Actual: ${line.length}`,
      );
    }

    // Validar cada campo
    for (const field of fields) {
      const startPos = field.position - 1; // Convertir a base 0
      const endPos = startPos + field.length;

      // Verificar que la posición existe en la línea
      if (startPos >= line.length) {
        errors.push(`${lineType}: Campo '${field.name}' fuera de rango`);

        continue;
      }

      const fieldValue = line.substring(startPos, Math.min(endPos, line.length));

      // Validar campo requerido
      if (field.required && fieldValue.trim() === '') {
        errors.push(`${lineType}: Campo requerido '${field.name}' está vacío`);
      }

      // Validar tipo de dato
      const typeError = this.validateFieldType(fieldValue, field, lineType);
      if (typeError) {
        errors.push(typeError);
      }
    }

    return errors;
  }
  private validateFieldType(value: string, field: Field, lineType: string): string | null {
    const trimmedValue = value.trim();

    if (trimmedValue === '' && !field.required) {
      return null; // Campo opcional vacío es válido
    }

    switch (field.type) {
      case 'number':
        if (isNaN(Number(trimmedValue))) {
          return `${lineType}: Campo '${field.name}' debe ser numérico. Valor: '${trimmedValue}'`;
        }
        break;

      case 'date':
        // Ejemplo para formato YYYYMMDD
        const datePattern = /^\d{8}$/;
        if (!datePattern.test(trimmedValue)) {
          return `${lineType}: Campo '${field.name}' debe tener formato YYYYMMDD. Valor: '${trimmedValue}'`;
        }
        break;

      case 'string':
        // Validar caracteres permitidos si es necesario
        break;
    }

    return null;
  }

  validateFileStructure(
    file: File,
    format: FileFormat,
  ): Promise<{ isValid: boolean; errors: string[] }> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      const errors: string[] = [];

      reader.onload = (event) => {
        const content = event.target?.result as string;
        const lines = content.split('\n').filter((line) => line.trim() !== '');

        // Validar que hay al menos una línea
        if (lines.length === 0) {
          errors.push('El archivo está vacío');
          resolve({ isValid: false, errors });
          return;
        }

        // Validar header (línea 1)
        // if (lines.length > 0) {
        //   const headerErrors = this.validateLine(lines[0], format.header, 'Header', format.lineLengthHeader);
        //   errors.push(...headerErrors);
        // }

        // Validar líneas de detalle (línea 2 en adelante)
        for (let i = 1; i < lines.length; i++) {
          const lineErrors = this.validateLine(
            lines[i],
            format.fields,
            `Línea ${i + 1}`,
            format.lineLengthDetails,
          );
          errors.push(...lineErrors);

          // Si hay muchos errores, detener validación
          if (errors.length > 1) {
            errors.push('Demasiados errores. Validación detenida.');
            break;
          }
        }

        resolve({ isValid: errors.length === 0, errors });
      };

      reader.readAsText(file);
    });
  }

  async generateFileHash(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (event) => {
        try {
          const content = event.target?.result as string;

          // Convertir el contenido a bytes
          const encoder = new TextEncoder();
          const data = encoder.encode(content);

          // Generar hash SHA-256
          const hashBuffer = await crypto.subtle.digest('SHA-256', data);

          // Convertir a hexadecimal
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

          resolve(hashHex);
        } catch (error) {
          reject(new Error(`Error al generar hash: ${error}`));
        }
      };

      reader.onerror = () => {
        reject(new Error('Error al leer el archivo'));
      };

      reader.readAsText(file);
    });
  }
}
